import { computedFrom } from "aurelia-binding";
import { EventAggregator } from "aurelia-event-aggregator";
import * as Enumerable from "linq";
import { MepInsightsRequest } from "../../../../../../../../models/mep/mep-insights.models";
import { MepTopVesselsEmissionsPerformers, MepDashboardRequest, MepTabsEnum, MepEventMessages, EmissionTypeCategoryEnum } from "../../../../../../../../models/mep/mep.models";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { BarChartViewModel, ChartItem } from "../../../../common-vm/bar-chart-view-model";
import { MepDashboardFilter } from "../mep-dashboard-filter/mep-dashboard-filter";
import { autoinject, useView } from 'aurelia-framework';

@autoinject
@useView('views/pages/modules/mep/tabs/mep-dashboard/partials/mep-top-contributors/mep-top-contributors.html')
export class MepTopContributors {
    ghgChartViewModel: BarChartViewModel;
    pollutantsChartViewModel: BarChartViewModel;
    isTopContributorsLoading: boolean = false;
    topContributors: MepTopVesselsEmissionsPerformers[];
    portId: number;
    filterViewModel: MepDashboardFilter;
    isMepTrial: boolean = false;
    isGhgEmissions: boolean = true;
    ghgEmissionsItems: MepTopVesselsEmissionsPerformers[];
    criteriaPollutantsItems: MepTopVesselsEmissionsPerformers[]
    distributionColElement: HTMLDivElement;

    @computedFrom('isGhgEmissions', 'ghgChartViewModel', 'pollutantsChartViewModel')
    public get chartViewModel(): BarChartViewModel {
        return this.isGhgEmissions ? this.ghgChartViewModel : this.pollutantsChartViewModel;
    }

    @computedFrom('isGhgEmissions', 'ghgChartViewModel', 'pollutantsChartViewModel')
    public get chartViewModels(): BarChartViewModel[] {
        return [this.ghgChartViewModel, this.pollutantsChartViewModel];
    }

    @computedFrom('ghgChartViewModel', 'pollutantsChartViewModel')
    public get hasData(): boolean {
        return (this.ghgChartViewModel && this.ghgChartViewModel.chartItems.length > 0) ||
            (this.pollutantsChartViewModel && this.pollutantsChartViewModel.chartItems.length > 0);
    }

    constructor(private mepService: MepService, private eventAggregator: EventAggregator, portId: number, filterViewModel: MepDashboardFilter) {
        this.portId = portId;
        this.filterViewModel = filterViewModel;
    }
    
    getTopVesselsEmissionsPerformers(request: MepDashboardRequest) {
        return new Promise<string>((resolve, reject) => {
            this.isTopContributorsLoading = true;

            if (this.filterViewModel.emissionTypeCategoryIds.length == 1) {
                this.isGhgEmissions = this.filterViewModel.emissionTypeCategoryIds[0] == EmissionTypeCategoryEnum.GhgEmissions;
            }
            
            this.mepService.getMepTopVesselsEmissionsPerformersForPortDashboard(request).then(response => {
                this.topContributors = response;
                this.ghgEmissionsItems = response.filter(x => x.emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions);
                this.criteriaPollutantsItems = response.filter(x => x.emissionTypeCategoryId == EmissionTypeCategoryEnum.CriteriaPollutants);
                this.initTopContributorsChart(true);
                this.initTopContributorsChart(false);
                this.isTopContributorsLoading = false;
                resolve(null);
            }).catch(() => {
                this.isTopContributorsLoading = false;
                reject("MEP Top Contributors to Emissions - Error retrieving the top contributors to emissions!");
            });
        });
    }

    initTopContributorsChart(isGhg: boolean) {
        const chartItems: ChartItem[] = [];
        let maxSizeValue = 0;

        const topContributors = this.topContributors.filter(x => x.emissionTypeCategoryId == (isGhg ? EmissionTypeCategoryEnum.GhgEmissions : EmissionTypeCategoryEnum.CriteriaPollutants));
        maxSizeValue = topContributors && topContributors.length > 0 ? Enumerable.from(topContributors).max(x => x.emissionValueTonnes) : 0;

        topContributors.forEach(contributor => {
            const chartItem: ChartItem = {
                id: contributor.vesselId,
                itemName: contributor.vesselName,
                additionalName: contributor.imo.toString(),
                bars: [{
                    id: contributor.vesselId,
                    barName: contributor.vesselName,
                    value: contributor.emissionValueTonnes,
                    size: contributor.emissionValueTonnes
                }]
            }
            chartItems.push(chartItem);
        });

        const units = isGhg ? 'tCO<sub>2</sub>e_100yr':'tonnes';
        const chartViewModel = new BarChartViewModel(chartItems, maxSizeValue * 1.1, 4, units);

        if (isGhg) {
            this.ghgChartViewModel = chartViewModel;
            this.ghgChartViewModel.description = "GHG Emissions";
        } else {
            this.pollutantsChartViewModel = chartViewModel;
            this.pollutantsChartViewModel.description = "Criteria Pollutants";
        }

        const vessels = Enumerable.from(topContributors).select(x => {
            return {
                id: x.vesselId,
                name: x.vesselName
            }
        }).toArray();
        chartViewModel.chartLegend.generateLegendItems(vessels);
    }

    /**
     * Get units value
     * @param isGhgEmissions
     */
    getUnits = (isGhgEmissions: boolean) => isGhgEmissions ? 'GHG Emissions (tCO<sub>2</sub>e_100yr)':'Emissions (tonnes)';

    clearData() {
        this.ghgChartViewModel = null;
        this.pollutantsChartViewModel = null;
        this.topContributors = [];
    }

    navigateToInsights() {
        const dashboardRequest = this.filterViewModel.getRequest();

        const request: MepInsightsRequest = {
            portId: dashboardRequest.portId,
            pointOfInterestIds: dashboardRequest.pointOfInterestIds,
            vesselTypeGroupIds: dashboardRequest.vesselTypeGroupIds,
            operationModeIds: dashboardRequest.operationModeIds,
            cargoTypeIds: dashboardRequest.cargoTypeIds,
            emissionTypeId: dashboardRequest.emissionTypeIds.length == 1 ? dashboardRequest.emissionTypeIds[0] : null,
            year: dashboardRequest.year,
            months: dashboardRequest.months,
            scopeIds: [],
            ghgRatings: null,
            weightBucketIds: null,
            ageFrom: null,
            ageTo: null
        }

        request.step = MepTabsEnum.Insights;
        this.eventAggregator.publish(MepEventMessages.ChangeMepTab, request);
    }

    setIsGhgEmission(value: boolean) {
        this.isGhgEmissions = value;
    }

    navigateToVesselsTab(item: ChartItem) {
        this.eventAggregator.publish(MepEventMessages.ChangeMepTab, {
            step: MepTabsEnum.Vessels,
            imo: item.additionalName
        });
    }
}